import React, { useEffect, useRef, useState } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { FiPlay } from "react-icons/fi";
import { MdFiberManualRecord } from "react-icons/md";

import { DEFAULT_CODEC_PREFERENCES, DEFAULT_EDGES } from "../../../constants";
import { useAppState } from "../../../context/StateContext";
import Alert from "../../Alert/Alert";
import useTestRunner from "../../AudioDeviceTestWidget/useTestRunner/useTestRunner";
import Button, { Color } from "../../Form/Button";
import SummaryWidget from "../../SummaryWidget/SummaryWidget";
import EdgeResult from "../NetworkTestWidget/EdgeResult/EdgeResult";
import useTestNetworkRunner from "../NetworkTestWidget/useTestRunner/useTestRunner";
import Device from "./Device";
import useStyles from "./styles";

const initialSettings = {
  edges: DEFAULT_EDGES,
  codecPreferences: DEFAULT_CODEC_PREFERENCES,
};

type DiagnosticsTestProps = {
  voiceToken?: string;
  iceServers?: RTCIceServer[];
  isJoinVideoSurvey?: boolean;
};

const DiagnosticsTest = ({
  iceServers,
  isJoinVideoSurvey = false,
  voiceToken,
}: DiagnosticsTestProps) => {
  const { t: translate } = useTranslation();

  const {
    error,
    warning,
    inputLevel,
    isRecording,
    isAudioInputTestRunning,
    isAudioOutputTestRunning,
    outputLevel,
    playAudio,
    playbackURI,
    readAudioInput,
    testEnded,
  } = useTestRunner();

  const { isRunning, results, activeEdge, runTests, activeTest } = useTestNetworkRunner();

  const { activeSinkId, setActiveSinkId, inputDeviceId, setInputDeviceId } = useAppState();

  const classes = useStyles({
    isRecording,
    isPlaying: isAudioOutputTestRunning,
    isJoinVideoSurvey,
  })();

  const disableAll = isRecording || isAudioOutputTestRunning || !!error;

  const previousInputDeviceIdRef = useRef("");

  const [resultsTest, setResults] = useState<any[]>([]);
  const [settings] = useState(initialSettings);

  async function startTest() {
    const testResults = await runTests(
      getVoiceToken,
      getTURNCredentials,
      settings.edges,
      settings.codecPreferences
    );

    setResults(testResults);
  }

  const getTURNCredentials = async () => {
    return iceServers || [];
  };

  const getVoiceToken = async () => {
    return voiceToken || "";
  };

  const handleRecordClick = () => {
    readAudioInput({ deviceId: inputDeviceId, enableRecording: true });
  };

  const handlePlayClick = () => {
    playAudio({
      deviceId: activeSinkId !== "default" ? activeSinkId : "",
      testURI: playbackURI,
    });
  };

  useEffect(() => {
    const newDeviceSelected = previousInputDeviceIdRef.current !== inputDeviceId;

    previousInputDeviceIdRef.current = inputDeviceId;
    if (!error && (newDeviceSelected || (!isRecording && !isAudioInputTestRunning))) {
      readAudioInput({ deviceId: inputDeviceId });
    }
  }, [error, inputDeviceId, isRecording, isAudioInputTestRunning, readAudioInput]);

  return (
    <section className={classes.section}>
      {!isJoinVideoSurvey && (
        <h3 data-cy="diagnosticsTestTitle" className={classes.modalTitle}>
          {translate("settings")}
        </h3>
      )}

      <header className={classes.header}>
        <Grid container direction="row" alignItems="center" justifyContent="space-between">
          <Grid item md={6} lg={6}>
            <span className={classes.audioDeviceText}>{translate("audioDeviceTest")}</span>
          </Grid>

          <Grid item container md={6} lg={6} justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button
                disabled={disableAll}
                className={classes.buttonRecord}
                customColor={Color.SECONDARY}
                onClick={handleRecordClick}
              >
                <MdFiberManualRecord size={18} className="mr-2" />

                {isRecording ? translate("recording") : translate("record")}
              </Button>
            </Grid>

            <Grid item>
              <Button
                disabled={!playbackURI || disableAll}
                className={classes.buttonPlay}
                customColor={Color.INFO}
                onClick={handlePlayClick}
              >
                <FiPlay size={18} className="mr-2" />

                {isAudioOutputTestRunning ? translate("playing") : translate("play")}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </header>

      {!!error && (
        <div className="mt-2">
          <Alert variant="error">
            <span>{error}</span>
          </Alert>
        </div>
      )}

      {!!warning && (
        <div className="mt-2">
          <Alert variant="warning">
            <span>{warning}</span>
          </Alert>
        </div>
      )}

      {testEnded && !error && !warning && (
        <div className="mt-2">
          <Alert variant="success">
            <span>{translate("noIssuesDetected")}</span>
          </Alert>
        </div>
      )}

      <hr className={classes.divider} />

      <Device
        deviceTitle={translate("microphone")}
        label={translate("inputDevice")}
        deviceType="audioinput"
        progressTitle={translate("inputLevel")}
        value={inputDeviceId}
        disabled={disableAll}
        deviceLevel={inputLevel}
        setDeviceId={(value) => setInputDeviceId(value)}
      />

      <section className="microphone-section mt-4">
        <Device
          deviceTitle={translate("speaker")}
          label={translate("outputDevice")}
          deviceType="audiooutput"
          progressTitle={translate("outputLevel")}
          value={activeSinkId}
          disabled={disableAll}
          deviceLevel={outputLevel}
          setDeviceId={(value) => setActiveSinkId(value)}
        />
      </section>

      <hr className="mb-2" />

      <section className="w-100">
        <h3 className={classes.deviceTitle}>{translate("connectivityAndBandwidthTest")}</h3>

        <Grid container direction="row" alignItems="center" spacing={2}>
          <Grid item md={10} lg={10}>
            {settings.edges.map((edge, i) => (
              <EdgeResult
                key={edge}
                codecPreferences={settings.codecPreferences}
                edge={edge}
                isActive={activeEdge === edge}
                result={results[i]}
                activeTest={activeTest}
              />
            ))}
          </Grid>

          <Grid item container md={2} lg={2} justifyContent="flex-end">
            <Button customColor={Color.SECONDARY} onClick={startTest} disabled={isRunning}>
              {translate("start")}
            </Button>
          </Grid>
        </Grid>

        <div className="mt-3">
          <SummaryWidget results={resultsTest} />
        </div>
      </section>
    </section>
  );
};

export default DiagnosticsTest;
